import React, {
  useCallback,
  useContext,
} from 'react'

import PropTypes from 'prop-types'
import classnames from 'classnames'

import RadioGroupContext from '../context'

import Styles from './styles.module.sass'


export default function RadioButton({ label, value, className, disabled, ...props }) {
  const context = useContext(RadioGroupContext)

  if ( !context ) {
    throw new Error('<RadioButton /> must be rendered inside a <RadioGroup />')
  }

  const {
    groupDisabled,
    handleChange,
    name,
    selectedValue,
  } = context

  const checked = ( selectedValue === value )
  const isDisabled = ( groupDisabled || disabled )

  const onChange = useCallback((e) => {
    e.stopPropagation()

    handleChange({
      checked: true,
      value,
    })
  }, [handleChange, value])

  const onKeyUp = useCallback((e) => {
    if ( isDisabled || e.target !== e.currentTarget || e.key !== ' ' ) {
      return
    }

    onChange(e)
  }, [isDisabled, onChange])

  const rootClassName = classnames(Styles.root, className, {
    [Styles.disabled]: isDisabled,
  })

  const radioClassName = classnames(Styles.radio, {
    [Styles.checked]: checked,
    [Styles.disabled]: isDisabled,
  })

  const tabIndex = ( isDisabled ) ? -1 : 0

  return (
    // biome-ignore lint/a11y/useSemanticElements: label with role="radio" preserves styling
    <label
      tabIndex={ tabIndex }
      { ...props }
      aria-checked={ checked }
      aria-disabled={ isDisabled }
      className={ rootClassName }
      onKeyDown={ onKeyDown }
      onKeyUp={ onKeyUp }
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: label with role="radio" preserves styling
      role="radio"
    >
      <input
        checked={ checked }
        disabled={ isDisabled }
        className={ Styles.input }
        name={ name }
        onChange={ onChange }
        type="radio"
        value={ value }
      />
      <div className={ radioClassName }>
        <span className={ Styles.radio__inner } />
      </div>
      { label }
    </label>
  )
}

RadioButton.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,

  className: PropTypes.string,
  disabled: PropTypes.bool,
}

// The browser scrolls the page on a Space keydown, before onKeyUp()
// gets a chance to act on the key. The keydown is handled here, so it
// doesn't propagate either. Keys aimed at an interactive element
// inside the label, such as a text input, are left alone.
//
function onKeyDown(e) {
  if ( e.target === e.currentTarget && e.key === ' ' ) {
    e.preventDefault()
    e.stopPropagation()
  }
}
